import React, {createContext, ReactNode, useCallback, useContext, useState} from 'react';

export type ReviewsModalSource = 'all-reviews' | 'variation-reviews' | null;

export interface CLPContext {
    isReviewsModalOpen: boolean;
    reviewsModalSource: ReviewsModalSource;
    openReviewsModal: (source?: ReviewsModalSource) => void;
    closeReviewsModal: () => void;
    toggleReviewsModal: () => void;
}

const CLPContext = createContext<CLPContext | undefined>(undefined);

interface ClpContextProviderProps {
    children: ReactNode;
}

export const ClpContextProvider: React.FC<ClpContextProviderProps> = ({children}) => {
    const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);
    const [reviewsModalSource, setReviewsModalSource] = useState<ReviewsModalSource>(null);

    const openReviewsModal = useCallback((source: ReviewsModalSource = 'variation-reviews') => {
        setReviewsModalSource(source);
        setIsReviewsModalOpen(true);
    }, []);

    const closeReviewsModal = useCallback(() => {
        setIsReviewsModalOpen(false);
        setReviewsModalSource(null);
    }, []);

    const toggleReviewsModal = useCallback(() => {
        setIsReviewsModalOpen((prev) => !prev);
    }, []);

    return (
        <CLPContext.Provider
            value={{
                isReviewsModalOpen,
                reviewsModalSource,
                openReviewsModal,
                closeReviewsModal,
                toggleReviewsModal,
            }}
        >
            {children}
        </CLPContext.Provider>
    );
};

export function useClpContext(): CLPContext {
    const context = useContext(CLPContext);
    if (!context) {
        throw new Error('useClpContext must be used within a ClpContextProvider');
    }
    return context;
}
